import { useQuery } from '@tanstack/react-query';
import { useKnowledgeService, DocumentMetadata } from '../../knowledge/services/knowledgeService';

export interface Guide {
  key: string;
  title: string;
  description: string;
  order: number;
}

const DOCS_PREFIX = 'docs/guides/gui/';

// Help docs are global (`cloud2` KB), static, and read-only — ideal cache
// entries. Guide list and each guide's content are separate keys.
export const helpKeys = {
  guides: () => ['help', 'guides'] as const,
  content: (guideKey: string) => ['help', 'guide', guideKey] as const,
};

export function useHelpGuides(enabled: boolean) {
  const knowledgeService = useKnowledgeService();

  return useQuery({
    queryKey: helpKeys.guides(),
    queryFn: async () => {
      const response = await knowledgeService.listDocuments('cloud2', DOCS_PREFIX);
      return response.documents
        .filter((doc: DocumentMetadata) => doc.key.endsWith('.md') && /\/\d{2}-/.test(doc.key))
        .map((doc: DocumentMetadata) => ({
          key: doc.key,
          title: doc.title || doc.key.split('/').pop()?.replace('.md', '') || '',
          description: doc.description || '',
          order: doc.order ?? 999,
        })) as Guide[];
    },
    enabled,
  });
}

export function useHelpGuideContent(guideKey: string | undefined) {
  const knowledgeService = useKnowledgeService();

  return useQuery({
    queryKey: helpKeys.content(guideKey as string),
    queryFn: async () => {
      const response = await knowledgeService.getDocumentContent('cloud2', guideKey as string);
      return response.content;
    },
    enabled: !!guideKey,
  });
}
